/**
 * The one line a technician says that is not an answer.
 *
 * Everything else this system produces answers a student, the knock included
 * - that one speaks first, but it still speaks AT them. A technician who
 * asks WHICH WAY IT GOES before the tool reports back is doing the thing an
 * instructor does and an excellent manual cannot: turning a number the
 * student is about to read into a number that either confirmed or
 * contradicted something they said out loud. Those are not the same memory.
 *
 * THE MARKER. `[[ask: which way does the rate go if you raise the power?]]`,
 * on a reply that is ALSO running something. Without a run there is nothing
 * coming to compare it against, and a prediction with no observation behind
 * it is a quiz question - so the marker is only taken where a run was taken,
 * and is otherwise stripped and forgotten. It still leaves the sentence
 * either way: brackets are not speech, and the line is about to be read
 * aloud.
 *
 * WHY THIS ONE NEEDS NO WHITELIST, unlike the other two. `[[show:]]` mounts
 * a scene and `[[run:]]` executes against a solver, so each resolves against
 * a list the brief handed out before anything happens (see demos.ts). This
 * marker does neither. It opens the text box and holds a report back for a
 * few seconds; the worst a badly chosen question can do is be a bad
 * question. What the student types in reply is not routed anywhere either -
 * it goes to the transcript and to the journal, and never to the model as an
 * instruction.
 *
 * WHAT IS NOT HERE, deliberately: any judgement of whether they were right.
 * Nothing in this file, or anywhere downstream, compares what the student
 * said to what the tool measured. The journal records the pair and the brief
 * carries the pair, and the person who says whether it matched is the
 * technician, out loud, from two facts she can both see - which is the same
 * line `work.ts` already draws. A `right: boolean` computed by matching prose
 * against a finding would be a graded answer with nobody accountable for the
 * grade, and it would be invisible when wrong.
 */

import type { ExperimentReport } from '../assistant/types'

/**
 * Longest prediction kept. The ask box takes 500 like any other line, but
 * what is stored is a claim, and the claim is in the first sentence; the
 * rest is the student thinking aloud, which is worth reading and not worth
 * carrying in every brief for the next hour.
 */
export const PREDICTION_MAX = 160

/**
 * How much of the tool's answer is kept beside the call.
 *
 * The findings, in the order the report gives them, cut at a WHOLE finding -
 * never one of them chosen. Two live runs taught this budget the hard way.
 * Keeping `findings[0]` alone came first, and the very first run showed why
 * that is wrong: she asked which way SELECTIVITY went and the headline
 * finding was about rate. Cutting a joined string at 400 characters came
 * next, and the second run showed why THAT is wrong: some reports open with
 * a sentence about how the module is solved, and 248 characters of preamble
 * ate the budget before the answer got in.
 *
 * Picking the finding that looks like it answers the question would fix
 * both, and is a match on prose - the same move this file refuses for
 * grading, failing the same way: silently, and only in the cases that
 * mattered. So the rule is dumb on purpose. Take findings until the budget
 * is gone, and never fewer than `MEASURED_MIN` of them, so a preamble
 * cannot be the whole of what the record remembers.
 */
export const MEASURED_MAX = 600
export const MEASURED_MIN = 2

/**
 * What the tool said, for the record beside what the student called.
 *
 * `findings` are written by the tool's own assistant from the numbers the
 * runs returned - the same sentences the student is about to read. All of
 * them, in order, quoted rather than summarised: nothing here re-derives a
 * value, and nothing here picks which finding was the relevant one. See
 * MEASURED_MAX in predict.ts for why choosing would be the wrong move.
 */
export function measured(report: ExperimentReport): string {
  const kept: string[] = []
  for (const finding of report.findings) {
    // Whole findings only, and the first few whatever they cost: a report
    // that opens by explaining how it was solved would otherwise spend the
    // record on its own preamble. See MEASURED_MAX above.
    if (kept.length >= MEASURED_MIN && kept.join(' ').length + finding.length > MEASURED_MAX) break
    kept.push(finding)
  }
  return kept.join(' ') || report.understood
}

const ASK = /\[\[\s*ask\s*:\s*([^\]]+?)\s*\]\]/gi

/**
 * Take the prediction question out of a reply.
 *
 * `running` is whether this same reply also took a run (`takeRun`). It is
 * the whole of the permission: a prediction is held against an observation,
 * and there is no observation without a run.
 */
export function takeAsk(text: string, running: boolean): { text: string; ask?: string } {
  let ask: string | undefined
  const stripped = text
    .replace(ASK, (_match, question: string) => {
      if (running && ask === undefined) ask = question.trim()
      return ''
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return ask ? { text: stripped, ask } : { text: stripped }
}
